// PRD.316 — Visit Note Clinical Findings.
//
// Clinician-confirmed encounter documentation, with OPTIONAL dental context.
// The section is generic: a finding with no teeth is an ordinary narrative, so
// non-dental disciplines can enable it without inheriting tooth semantics.
//
// The relationship with the Part Chart is one-way and always explicit:
//
//     Part Chart  ──(suggestion)──▶  [clinician accepts]  ──▶  Clinical Finding
//
// Nothing is written until a suggestion is accepted; after that the narrative is
// documentation that later chart edits never touch, and editing it never writes
// back to `odontogram_charts`.
//
// Reuse: the narrative editor is PRD.314's `HistoryEntryEditor`, so autosave,
// the identity-captured stale-write guards and the shared voice pipeline are
// literally the same code Medical History runs — no second transcription path.

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  buildChartSuggestions,
  filterAcceptedSuggestions,
  formatSuggestionLabel,
  formatToothContext,
  FINDING_TEXTAREA_MAX_ROWS,
  getMedicalHistorySettings,
  parseFindingTeeth,
  parseToothStatesMap,
  qAppConfig,
  qClinicalFindingsByEncounter,
  qOdontogramChartsByEncounter,
  serializeFindingTeeth,
  sortFindings,
  suggestionToFindingDraft,
  type AppConfigRow,
  type ChartSuggestion,
  type ClinicalFindingRow,
  type FindingTooth,
  type OdontogramChartRow,
  type PatientEncounterRow,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { HistoryEntryEditor } from './HistoryEntryEditor'; // PRD.314
import { ToothPickerModal } from './ToothPickerModal';

/**
 * Distance from the top of a finding row to the top of the narrative's first
 * line box: the compact textarea's 1px border plus its 6px top padding.
 *
 * The tooth/area label and the remove button both offset by this so they line
 * up with the first line of text — not with the top of the input — and stay
 * aligned when the narrative grows to several rows.
 */
const FIRST_LINE_OFFSET = 7;

export function ClinicalFindingsSection({
  encounter,
  patientId,
}: {
  encounter: PatientEncounterRow;
  patientId: string;
}) {
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';

  const [toothPickerFor, setToothPickerFor] = useState<string | null>(null);

  /**
   * The one unpersisted finding "+ Add Finding" opens. It lives in memory until
   * it has narrative text (see `saveNarrative`), so an abandoned click never
   * leaves a blank record behind.
   */
  const [draft, setDraft] = useState<{
    id: string;
    sortOrder: number;
    teeth?: FindingTooth[];
  } | null>(null);
  /**
   * Metadata for every id that has been handed to an editor but not yet
   * INSERTed, keyed by id rather than holding only the current draft.
   *
   * A single `draft` ref would lose text in this sequence: type into draft A,
   * click "+ Add Finding" again before the 600 ms autosave fires, A unmounts
   * and flushes — by then the ref points at draft B, so A's save would fall
   * through to an UPDATE against a row that does not exist and the text would
   * vanish. Keyed lookup lets a superseded draft still promote itself.
   */
  const pendingDraftsRef = useRef(new Map<string, { sortOrder: number; teeth: FindingTooth[] }>());

  // Voice availability reuses the existing clinic capability switch (PRD §11 —
  // "respect any existing clinic/workspace capability setting"). It gates the
  // CONTROLS only; dictated text is ordinary narrative afterwards.
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const settings = useMemo(
    () => getMedicalHistorySettings(appConfigRows[0] ?? null),
    [appConfigRows],
  );

  const { data: findingRows = [] } = usePowerSyncQuery<ClinicalFindingRow>(
    qClinicalFindingsByEncounter(encounter.id),
  );
  const findings = useMemo(() => sortFindings(findingRows), [findingRows]);

  // PRD §8.5 — only the ACTIVE encounter's charts feed suggestions. Offering the
  // patient's entire charting history would turn every past condition into a
  // current-visit prompt.
  const { data: chartRows = [] } = usePowerSyncQuery<OdontogramChartRow>(
    qOdontogramChartsByEncounter(encounter.id),
  );

  const suggestions = useMemo(() => {
    const all: ChartSuggestion[] = [];
    for (const chart of chartRows) {
      all.push(...buildChartSuggestions(chart.id, parseToothStatesMap(chart.tooth_states)));
    }
    return filterAcceptedSuggestions(all, findings);
  }, [chartRows, findings]);

  // ── Writes ──

  const nextSortOrder = useCallback(
    () => findings.reduce((max, f) => Math.max(max, Number(f.sort_order ?? 0)), 0) + 1,
    [findings],
  );

  const insertFinding = useCallback(
    async (draft: {
      text: string;
      teeth: FindingTooth[];
      source: 'manual' | 'chart';
      sourceRef: string | null;
      conditionCode: string | null;
    }) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO encounter_clinical_findings
           (id, org_id, clinic_id, patient_id, encounter_id, text, teeth, source, source_ref,
            condition_code, sort_order, created_by_user_id, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          null,
          patientId,
          encounter.id,
          draft.text || null,
          serializeFindingTeeth(draft.teeth),
          draft.source,
          draft.sourceRef,
          draft.conditionCode,
          nextSortOrder(),
          user?.uid ?? null,
          now,
          now,
        ],
      );
    },
    [db, orgId, patientId, encounter.id, user?.uid, nextSortOrder],
  );

  /**
   * Accept a chart suggestion (PRD §8.2). This is the ONLY moment chart context
   * becomes documentation — until now nothing about the suggestion was stored.
   */
  const acceptSuggestion = useCallback(
    async (s: ChartSuggestion) => {
      const draft = suggestionToFindingDraft(s);
      await insertFinding({
        text: draft.text,
        teeth: draft.teeth,
        source: 'chart',
        sourceRef: draft.sourceRef,
        conditionCode: draft.conditionCode,
      });
    },
    [insertFinding],
  );

  /**
   * "+ Add Finding" opens an UNPERSISTED row.
   *
   * It used to INSERT immediately, which meant a clinician who clicked the
   * button and then walked away left a blank General finding in the record —
   * and it came back on every reload, reading as though the app had invented
   * it. Same rule as PRD.314's history editors: no row until there is
   * something to store. The id is generated up front so the editor's identity
   * is stable across the draft → persisted transition and typing is never
   * interrupted.
   */
  const addManualFinding = useCallback(() => {
    const id = crypto.randomUUID();
    const sortOrder = nextSortOrder();
    pendingDraftsRef.current.set(id, { sortOrder, teeth: [] });
    setDraft({ id, sortOrder });
  }, [nextSortOrder]);

  /**
   * Persist one finding's narrative.
   *
   * `identity` is the finding id captured when the edit began, so a save that
   * resolves after the clinician moved on still lands on the right row. Writes
   * ONLY this table — the odontogram is never touched (PRD §8.3).
   */
  const saveNarrative = useCallback(
    async (text: string, findingId: string) => {
      if (!db) return;
      const now = new Date().toISOString();

      // A draft becomes a real record the moment it has meaningful content.
      // An empty draft stays unpersisted, so nothing is written for a finding
      // the clinician started and abandoned.
      const pending = pendingDraftsRef.current.get(findingId);
      if (pending) {
        if (!text.trim()) return;
        await db.execute(
          `INSERT INTO encounter_clinical_findings
             (id, org_id, clinic_id, patient_id, encounter_id, text, teeth, source, source_ref,
              condition_code, sort_order, created_by_user_id, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            findingId,
            orgId,
            null,
            patientId,
            encounter.id,
            text,
            serializeFindingTeeth(pending.teeth),
            'manual',
            null,
            null,
            pending.sortOrder,
            user?.uid ?? null,
            now,
            now,
          ],
        );
        pendingDraftsRef.current.delete(findingId);
        setDraft((d) => (d?.id === findingId ? null : d));
        return;
      }

      await db.execute(
        'UPDATE encounter_clinical_findings SET text = ?, updated_at = ? WHERE id = ?',
        [text.trim() ? text : null, now, findingId],
      );
    },
    [db, orgId, patientId, encounter.id, user?.uid],
  );

  const saveTeeth = useCallback(
    async (findingId: string, teeth: FindingTooth[]) => {
      // Choosing teeth on a still-empty draft is not yet documentation, so it
      // is held in memory and written with the narrative's first save.
      const pending = pendingDraftsRef.current.get(findingId);
      if (pending) {
        pending.teeth = teeth;
        setDraft((d) => (d?.id === findingId ? { ...d, teeth } : d));
        return;
      }
      if (!db) return;
      await db.execute(
        'UPDATE encounter_clinical_findings SET teeth = ?, updated_at = ? WHERE id = ?',
        [serializeFindingTeeth(teeth), new Date().toISOString(), findingId],
      );
    },
    [db],
  );

  // Hard DELETE, matching the Symptoms / Diagnoses chip convention on encounters.
  const removeFinding = useCallback(
    async (findingId: string) => {
      if (pendingDraftsRef.current.has(findingId)) {
        pendingDraftsRef.current.delete(findingId);
        setDraft((d) => (d?.id === findingId ? null : d));
        return;
      }
      if (!db) return;
      await db.execute('DELETE FROM encounter_clinical_findings WHERE id = ?', [findingId]);
    },
    [db],
  );

  /**
   * Rows to render: persisted findings plus the in-memory draft.
   *
   * The draft carries the id it will be INSERTed with, so when it is promoted
   * the key is unchanged, React keeps the same editor instance, and the
   * clinician's caret and in-flight text survive the transition.
   */
  const rows = useMemo(() => {
    const list: (ClinicalFindingRow & { isDraft?: boolean })[] = [...findings];
    if (draft && !findings.some((f) => f.id === draft.id)) {
      list.push({
        id: draft.id,
        org_id: orgId,
        clinic_id: null,
        patient_id: patientId,
        encounter_id: encounter.id,
        text: null,
        teeth: null,
        source: 'manual',
        source_ref: null,
        condition_code: null,
        sort_order: draft.sortOrder,
        created_at: null,
        updated_at: null,
        updated_by: null,
        isDraft: true,
      });
    }
    return list;
  }, [findings, draft, orgId, patientId, encounter.id]);

  const pickerFinding = rows.find((f) => f.id === toothPickerFor) ?? null;

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {/* ── Suggested from Chart ──
          Rendered from live chart state and NEVER persisted. Accepted items drop
          out of this strip because their `source_ref` matches a stored finding. */}
      {suggestions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-muted)',
            }}
          >
            Suggested from Chart
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
            {suggestions.map((s) => (
              <button
                key={s.ref}
                onClick={() => void acceptSuggestion(s)}
                title="Add as a clinical finding"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-full)',
                  border: `1px dashed ${primary}`,
                  background: 'transparent',
                  color: primary,
                  fontSize: 'var(--font-size-xs)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <span style={{ fontWeight: 600 }}>+</span>
                {formatSuggestionLabel(s)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Findings ──
          One compact ROW each, not a card: tooth context left, narrative taking
          the width, mic + remove right. A dental encounter can carry a dozen
          findings, so vertical cost per finding is the constraint. General and
          tooth-scoped findings share this structure exactly. */}
      {rows.length === 0 && suggestions.length === 0 && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          No findings recorded for this visit.
        </div>
      )}

      {rows.map((f) => {
        const teeth = f.isDraft ? (draft?.teeth ?? []) : parseFindingTeeth(f.teeth);
        const context = formatToothContext(teeth);
        const fromChart = f.source === 'chart' && !!f.condition_code;
        return (
          <div
            key={f.id}
            className="pc-finding-row"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--sp-2)',
              paddingBottom: 'var(--sp-2)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            {/* LEFT — tooth/area, with provenance demoted beneath it. */}
            <div
              className="pc-finding-context"
              style={{
                display: 'flex',
                flexDirection: 'column',
                paddingTop: FIRST_LINE_OFFSET,
              }}
            >
              <button
                onClick={() => setToothPickerFor(f.id)}
                title="Choose teeth for this finding"
                style={{
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  color: context ? 'var(--text)' : 'var(--text-muted)',
                  fontSize: 'var(--font-size-xs)',
                  lineHeight: 'var(--line-height-body)',
                  fontWeight: context ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                }}
              >
                {context || 'General'}
              </button>
              {/* Secondary metadata — present, but never competing with the
                  finding itself. The underlying provenance data is unchanged. */}
              {fromChart && (
                <span
                  title="Originated from the Part Chart"
                  style={{
                    fontSize: 'calc(var(--font-size-xs) * 0.85)',
                    lineHeight: 1.2,
                    marginTop: -2,
                    color: 'var(--text-placeholder, var(--text-muted))',
                    whiteSpace: 'nowrap',
                  }}
                >
                  From chart
                </span>
              )}
            </div>

            {/* CENTER — narrative gets the width. */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <HistoryEntryEditor
                identity={f.id}
                storedValue={f.text ?? ''}
                onSave={saveNarrative}
                label="Clinical finding"
                placeholder="Describe finding..."
                voiceEnabled={settings.voiceToTextEnabled}
                autoRowsMax={FINDING_TEXTAREA_MAX_ROWS}
              />
            </div>

            {/* RIGHT — the mic lives inside the editor; this is remove only. */}
            <button
              onClick={() => void removeFinding(f.id)}
              aria-label="Remove finding"
              title="Remove finding"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                // Icon box is 28px tall (20px glyph + 4px padding); centre it on
                // the first line box instead of hanging it from the row top.
                marginTop: FIRST_LINE_OFFSET + 4,
                padding: 4,
                border: 'none',
                background: 'transparent',
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              <NavIcon name="x" />
            </button>
          </div>
        );
      })}

      <div>
        <button
          onClick={() => void addManualFinding()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${primary}`,
            background: 'transparent',
            color: primary,
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <NavIcon name="plus" />
          Add Finding
        </button>
      </div>

      {pickerFinding && (
        <ToothPickerModal
          open
          initialTeeth={
            pickerFinding.id === draft?.id
              ? (draft.teeth ?? [])
              : parseFindingTeeth(pickerFinding.teeth)
          }
          toothStates={chartRows[0] ? parseToothStatesMap(chartRows[0].tooth_states) : {}}
          primary={primary}
          onClose={() => setToothPickerFor(null)}
          onSave={(teeth) => {
            void saveTeeth(pickerFinding.id, teeth);
            setToothPickerFor(null);
          }}
        />
      )}
    </div>
  );
}
